import { Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useProjects, useSubtasks, useTask } from "@/hooks/use-tasks";
import type { Task } from "@/lib/db";
import { formatClock, instantAt, minuteOfDay } from "@/lib/plan";
import { createSubtask, deleteTask, PRIORITY_LABELS, setDone, updateTask } from "@/lib/planner";
import { describeRule } from "@/lib/recurrence";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/ui";

const saveFailed = () => toast.error("Could not save on this device. Try again.");

async function save(id: string, patch: Partial<Task>) {
	try {
		await updateTask(id, patch);
	} catch {
		saveFailed();
	}
}

/**
 * Edits one task. Fields save as they change (on blur for text), straight to
 * Dexie and the outbox, so there is no Save button to forget.
 */
export function TaskSheet() {
	const openId = useUIStore((s) => s.openTaskId);
	const setOpenId = useUIStore((s) => s.setOpenTaskId);
	const task = useTask(openId);

	return (
		<Sheet open={!!openId && task !== null} onOpenChange={(open) => !open && setOpenId(null)}>
			<SheetContent className="w-full overflow-y-auto sm:max-w-md">
				{task && <TaskForm key={task.id} task={task} onDeleted={() => setOpenId(null)} />}
			</SheetContent>
		</Sheet>
	);
}

function TaskForm({ task, onDeleted }: { task: Task; onDeleted: () => void }) {
	const [title, setTitle] = useState(task.title);
	const [notes, setNotes] = useState(task.notes);
	const [estimate, setEstimate] = useState(task.estimate_min?.toString() ?? "");
	const [confirming, setConfirming] = useState(false);
	const time =
		task.due_at && task.due_date ? formatClock(minuteOfDay(task.due_at, task.due_date)) : "";

	function saveTitle() {
		const next = title.trim();
		if (!next) return setTitle(task.title);
		if (next !== task.title) void save(task.id, { title: next });
	}

	function saveEstimate() {
		const n = estimate.trim() === "" ? null : Math.round(Number(estimate));
		if (n !== null && (!Number.isFinite(n) || n < 1 || n > 1440)) {
			toast.error("Estimate must be 1 to 1440 minutes.");
			return setEstimate(task.estimate_min?.toString() ?? "");
		}
		if (n !== task.estimate_min) void save(task.id, { estimate_min: n });
	}

	function saveDate(date: string) {
		if (!date) {
			if (task.recurrence_rule) return toast.error("A repeating task needs a date.");
			return void save(task.id, { due_date: null, due_at: null, status: "inbox" });
		}
		// Keep the clock time when the day changes.
		const due_at =
			task.due_at && time ? instantAt(date, minuteOfDay(task.due_at, task.due_date!)) : null;
		void save(task.id, {
			due_date: date,
			due_at,
			status: task.status === "inbox" ? "todo" : task.status,
		});
	}

	function saveTime(value: string) {
		if (!task.due_date) return;
		if (!value) return void save(task.id, { due_at: null });
		const [h, m] = value.split(":").map(Number);
		void save(task.id, { due_at: instantAt(task.due_date, h * 60 + m) });
	}

	async function remove() {
		try {
			await deleteTask(task.id);
			setConfirming(false);
			onDeleted();
			toast.success("Task deleted", { description: task.title });
		} catch {
			saveFailed();
		}
	}

	return (
		<>
			<SheetHeader>
				<SheetTitle>Task</SheetTitle>
				<SheetDescription>
					{task.recurrence_rule ? describeRule(task.recurrence_rule) : "Changes save as you go."}
				</SheetDescription>
			</SheetHeader>
			<div className="grid gap-4 px-4 pb-4">
				<div className="grid gap-1.5">
					<Label htmlFor="task-title">Title</Label>
					<Input
						id="task-title"
						className="h-11"
						value={title}
						maxLength={500}
						onChange={(e) => setTitle(e.target.value)}
						onBlur={saveTitle}
						onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
					/>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<div className="grid gap-1.5">
						<Label htmlFor="task-date">Date</Label>
						<DatePicker id="task-date" clearable value={task.due_date ?? ""} onChange={saveDate} />
					</div>
					<div className="grid gap-1.5">
						<Label htmlFor="task-time">Time</Label>
						<Input
							id="task-time"
							type="time"
							step={900}
							className="h-11"
							value={time}
							disabled={!task.due_date}
							onChange={(e) => saveTime(e.target.value)}
						/>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<div className="grid gap-1.5">
						<Label htmlFor="task-priority">Priority</Label>
						<Select
							value={String(task.priority)}
							onValueChange={(v) => void save(task.id, { priority: Number(v) })}
						>
							<SelectTrigger id="task-priority" className="h-11 w-full">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{PRIORITY_LABELS.map((label, i) => (
									<SelectItem key={label} value={String(i)}>
										{label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="grid gap-1.5">
						<Label htmlFor="task-estimate">Estimate (min)</Label>
						<Input
							id="task-estimate"
							type="number"
							inputMode="numeric"
							min={1}
							max={1440}
							className="h-11"
							value={estimate}
							onChange={(e) => setEstimate(e.target.value)}
							onBlur={saveEstimate}
						/>
					</div>
				</div>

				<ProjectPicker task={task} />

				<div className="grid gap-1.5">
					<Label htmlFor="task-notes">Notes</Label>
					<Textarea
						id="task-notes"
						rows={5}
						value={notes}
						maxLength={20000}
						onChange={(e) => setNotes(e.target.value)}
						onBlur={() => notes !== task.notes && void save(task.id, { notes })}
					/>
				</div>

				{!task.parent_id && <Subtasks parent={task} />}

				<Button
					variant="ghost"
					className="text-destructive h-11 justify-self-start"
					onClick={() => setConfirming(true)}
				>
					<Trash2 /> Delete task
				</Button>
			</div>
			<ConfirmDialog
				open={confirming}
				onOpenChange={setConfirming}
				title="Delete this task?"
				description="It and its subtasks are removed on every device once it syncs. This cannot be undone."
				confirmLabel="Delete"
				onConfirm={() => void remove()}
			/>
		</>
	);
}

function Subtasks({ parent }: { parent: Task }) {
	const subtasks = useSubtasks(parent.id) ?? [];
	const [text, setText] = useState("");
	const done = subtasks.filter((t) => t.status === "done").length;

	async function add() {
		const title = text.trim();
		if (!title) return;
		// Clear at once so fast typing after Enter starts a fresh entry.
		setText("");
		try {
			await createSubtask(parent.id, title);
		} catch {
			setText(title);
			saveFailed();
		}
	}

	return (
		<section className="grid gap-1.5" aria-labelledby="subtasks-heading">
			<h3 id="subtasks-heading" className="text-sm font-medium">
				Subtasks{subtasks.length ? ` (${done}/${subtasks.length})` : ""}
			</h3>
			{subtasks.length > 0 && (
				<ul className="divide-y rounded-md border" aria-label="Subtasks">
					{subtasks.map((t) => (
						<li key={t.id} className="flex min-h-11 items-center gap-2 pl-2">
							<Checkbox
								className="size-5"
								checked={t.status === "done"}
								aria-label={`${t.status === "done" ? "Mark not done" : "Mark done"}: ${t.title}`}
								onCheckedChange={(v) => void setDone(t.id, v === true).catch(saveFailed)}
							/>
							<span
								className={cn(
									"min-w-0 flex-1 truncate text-sm",
									t.status === "done" && "text-muted-foreground line-through",
								)}
							>
								{t.title}
							</span>
							<Button
								variant="ghost"
								size="icon"
								className="size-11 md:size-9"
								aria-label={`Remove subtask: ${t.title}`}
								onClick={() => void deleteTask(t.id).catch(saveFailed)}
							>
								<X />
							</Button>
						</li>
					))}
				</ul>
			)}
			<form
				className="flex gap-2"
				onSubmit={(e) => {
					e.preventDefault();
					void add();
				}}
			>
				<Input
					className="h-11"
					placeholder="Add a subtask"
					aria-label="New subtask"
					maxLength={500}
					value={text}
					onChange={(e) => setText(e.target.value)}
				/>
				<Button
					type="submit"
					variant="outline"
					size="icon"
					className="size-11"
					aria-label="Add subtask"
					disabled={!text.trim()}
				>
					<Plus />
				</Button>
			</form>
		</section>
	);
}

const NO_PROJECT = "none";

function ProjectPicker({ task }: { task: Task }) {
	const projects = useProjects() ?? [];
	// Archived projects stay selectable only for tasks already in them.
	const options = projects.filter((p) => !p.archived || p.id === task.project_id);
	return (
		<div className="grid gap-1.5">
			<Label htmlFor="task-project">Project</Label>
			<Select
				value={task.project_id ?? NO_PROJECT}
				onValueChange={(v) => void save(task.id, { project_id: v === NO_PROJECT ? null : v })}
			>
				<SelectTrigger id="task-project" className="h-11 w-full">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={NO_PROJECT}>No project</SelectItem>
					{options.map((p) => (
						<SelectItem key={p.id} value={p.id}>
							{p.name}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{projects.length === 0 && (
				<p className="text-muted-foreground text-xs">Create projects in Settings.</p>
			)}
		</div>
	);
}
